<template>
  <div class="y-tabs" :class="{ 'single-tab': registeredTabs.length === 1 }">
    <div class="y-tabs-nav" ref="navRef">
      <div
        v-for="(tab, index) in registeredTabs"
        :key="tab.uid"
        class="y-tabs-item"
        :class="{ 'is-active': activeIndex === index }"
        @click="setActive(index)"
      >
        <span class="y-tabs-item-text">{{ tab.label }}</span>
      </div>
      <div class="y-tabs-indicator" :style="indicatorStyle" />
    </div>
    <div class="y-tabs-content">
      <slot />
    </div>
  </div>
</template>

<script setup>
import {
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  provide,
  reactive,
  ref,
  watch
} from "vue";

const props = defineProps({
  modelValue: { type: [String, Number], default: 0 }
});
const emit = defineEmits(["update:modelValue"]);

const navRef = ref(null);
const activeIndex = ref(props.modelValue);
const registeredTabs = reactive([]);
const indicatorStyle = ref({});

let isInitialized = false;
let visibilityObserver = null;

const setActive = index => {
  activeIndex.value = index;
  emit("update:modelValue", index);
};

const sortTabs = () => {
  registeredTabs.sort((a, b) => {
    if (!a.el || !b.el) return 0;
    const position = a.el.compareDocumentPosition(b.el);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
    return 0;
  });
};

const registerTab = tabInstance => {
  const exists = registeredTabs.some(t => t.uid === tabInstance.uid);
  if (!exists) {
    registeredTabs.push(tabInstance);
    nextTick(() => {
      sortTabs();
      if (isInitialized) {
        updateIndicator();
      }
    });
  }
};

const unregisterTab = uid => {
  const index = registeredTabs.findIndex(t => t.uid === uid);
  if (index !== -1) {
    registeredTabs.splice(index, 1);
  }
};

provide("registerTab", registerTab);
provide("activeIndex", activeIndex);
provide("registeredTabs", registeredTabs);
provide("unregisterTab", unregisterTab);

const updateIndicator = () => {
  if (!navRef.value || registeredTabs.length === 0 || activeIndex.value < 0) {
    return false;
  }

  const activeTabEl = navRef.value.children[activeIndex.value];
  if (!activeTabEl) return false;

  const textEl = activeTabEl.querySelector(".y-tabs-item-text");
  if (!textEl) return false;

  const navRect = navRef.value.getBoundingClientRect();

  if (navRect.width === 0) return false;

  const textRect = textEl.getBoundingClientRect();
  const textOffsetLeft = textRect.left - navRect.left;
  const textWidth = textRect.width;

  indicatorStyle.value = {
    width: `${textWidth}px`,
    transform: `translateX(${textOffsetLeft}px)`
  };

  return true;
};

const startObserver = () => {
  stopObserver();

  if (navRef.value) {
    visibilityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const success = updateIndicator();
        if (success) {
          isInitialized = true;
          stopObserver();
        }
      }
    });
    visibilityObserver.observe(navRef.value);
  }
};

const stopObserver = () => {
  if (visibilityObserver) {
    visibilityObserver.disconnect();
    visibilityObserver = null;
  }
};

watch(activeIndex, (newIndex, oldIndex) => {
  if (isInitialized && newIndex !== oldIndex) {
    nextTick(() => updateIndicator());
  }
});

watch(
  registeredTabs,
  () => {
    if (isInitialized) {
      nextTick(() => updateIndicator());
    }
  },
  { flush: "post" }
);

onMounted(() => {
  startObserver();
});

onUnmounted(() => {
  stopObserver();
});

onActivated(() => {
  isInitialized = false;
  startObserver();
});

onDeactivated(() => {
  stopObserver();
});
</script>

<style lang="scss" scoped>
.y-tabs {
  &.single-tab {
    .y-tabs-item {
      justify-content: flex-start;
      padding-left: 24px;
    }
    .y-tabs-nav {
      position: relative;
      justify-content: flex-start;
    }
  }
}

.y-tabs-nav {
  position: relative;
  display: flex;
  justify-content: center;
}

.y-tabs-item {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &.is-active {
    .y-tabs-item-text {
      color: #000b1e;
      font-family: var(--y-medium-font);
    }
  }

  .y-tabs-item-text {
    transition: color 0.3s;
    color: #0b0b0b;
    font-family: var(--y-regular-font);
  }
}

.y-tabs-indicator {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  background-color: #0b0b0b;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}
</style>
